import {
  AUTH_PROVIDER_HEADER,
  AuthProviderEnum,
  DEFAULT_AUTH_PROVIDER,
  FEATURE_FLAGS_STORAGE_KEY,
  TAuthProvider,
  USE_SUPABASE_AUTH_FLAG_KEY,
} from '@onesource/schemas';

/**
 * localStorage keys for auth-related storage
 */
export const SESSION_STORAGE_KEY = 'session';
export const USER_STORAGE_KEY = 'user';
export const LC_SOURCE_STORAGE_KEY = 'lc_auth_source';

/**
 * Get the active auth provider by reading USE_SUPABASE_AUTH from featureFlags in localStorage.
 * Falls back to VITE_AUTH_PROVIDER_DEFAULT env var, then 'wordpress'.
 */
export const getActiveAuthProvider = (): TAuthProvider => {
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (raw) {
      const flags = JSON.parse(raw);
      if (typeof flags === 'object' && flags !== null && USE_SUPABASE_AUTH_FLAG_KEY in flags) {
        return flags[USE_SUPABASE_AUTH_FLAG_KEY] ? AuthProviderEnum.supabase : AuthProviderEnum.wordpress;
      }
    }

    // Feature flag not set — fall back to env default
    const envDefault = import.meta.env.VITE_AUTH_PROVIDER_DEFAULT;
    if (envDefault === AuthProviderEnum.supabase || envDefault === AuthProviderEnum.wordpress) {
      return envDefault;
    }

    return DEFAULT_AUTH_PROVIDER;
  } catch {
    return DEFAULT_AUTH_PROVIDER;
  }
};

/**
 * Set the active auth provider by writing USE_SUPABASE_AUTH into featureFlags in localStorage.
 * Preserves other feature flags.
 */
export const setActiveAuthProvider = (provider: TAuthProvider): void => {
  try {
    let flags: Record<string, unknown> = {};
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        flags = parsed;
      }
    }
    flags[USE_SUPABASE_AUTH_FLAG_KEY] = provider === AuthProviderEnum.supabase;
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.error('Failed to save auth provider to localStorage:', error);
  }
};

/**
 * Clear auth session data (session tokens, user info, members-fe source).
 * When keepProvider is false (default), sets USE_SUPABASE_AUTH to false in featureFlags.
 * When keepProvider is true, leaves featureFlags untouched.
 */
export const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

export const clearAuthProvider = (opts?: { keepProvider?: boolean }): void => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(LC_SOURCE_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!opts?.keepProvider) {
      setActiveAuthProvider(AuthProviderEnum.wordpress);
    }
  } catch (error) {
    console.error('Failed to clear auth provider from localStorage:', error);
  }
};

/**
 * Remove the USE_SUPABASE_AUTH flag from featureFlags so getActiveAuthProvider
 * falls back to the VITE_AUTH_PROVIDER_DEFAULT env var.
 */
export const removeAuthFlag = (): void => {
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (raw) {
      const flags = JSON.parse(raw);
      if (typeof flags === 'object' && flags !== null && USE_SUPABASE_AUTH_FLAG_KEY in flags) {
        delete flags[USE_SUPABASE_AUTH_FLAG_KEY];
        localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
      }
    }
  } catch (error) {
    console.error('Failed to remove auth flag from localStorage:', error);
  }
};

/**
 * Check if Supabase auth is currently active
 */
export const isSupabaseAuthActive = (): boolean => {
  return getActiveAuthProvider() === AuthProviderEnum.supabase;
};

/**
 * Check if WordPress auth is currently active
 */
export const isWordPressAuthActive = (): boolean => {
  return getActiveAuthProvider() === AuthProviderEnum.wordpress;
};

/**
 * Get the auth provider header value for API requests
 */
export const getAuthProviderHeader = (): Record<string, string> => {
  return {
    [AUTH_PROVIDER_HEADER]: getActiveAuthProvider(),
  };
};

/**
 * WordPress members page URL for logout redirect
 */
const WP_MEMBERS_PAGE = 'https://members.laurenohayon.com';

let _skipMembersRedirect = false;

/**
 * Call once at app startup to prevent Supabase logout from redirecting to members-fe.
 * Used by dashboard which has its own login page.
 */
export const setSkipMembersRedirect = (skip: boolean): void => {
  _skipMembersRedirect = skip;
};

/**
 * Redirect to the appropriate page after logout based on auth provider.
 * - Supabase users → members-fe home (default, triggers SSO re-initiation)
 * - Supabase users with intentionalLogout → members-fe /auth/login (no SSO re-initiation)
 * - Supabase users when skipMembersRedirect is set → local /auth/login
 * - WordPress users → WordPress members page
 */
export const redirectForSSOLogin = (authProvider?: TAuthProvider): void => {
  const provider = authProvider ?? getActiveAuthProvider();

  if (provider !== AuthProviderEnum.supabase) {
    window.location.replace(WP_MEMBERS_PAGE);
    return;
  }

  const membersFeUrl = import.meta.env.VITE_MEMBERS_FE_URL as string | undefined;

  if (!_skipMembersRedirect && membersFeUrl) {
    window.location.href = membersFeUrl;
    return;
  }

  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login';
  }
};

export const redirectToMembers = (authProvider?: TAuthProvider): void => {
  const provider = authProvider ?? getActiveAuthProvider();

  if (provider !== AuthProviderEnum.supabase) {
    window.location.replace(WP_MEMBERS_PAGE);
    return;
  }

  const membersFeUrl = import.meta.env.VITE_MEMBERS_FE_URL as string | undefined;

  if (!_skipMembersRedirect && membersFeUrl) {
    window.location.href = `${membersFeUrl}/apps`;
    return;
  }

  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login';
  }
};
